import { useQueryClient } from "@tanstack/react-query";
import { updateTaskStatus, useTasks } from "../../lib/queries";
import { relativeTime } from "../../lib/format";
import type { TaskStatus } from "../../types/database";

const STATUSES: TaskStatus[] = ["todo", "in_progress", "blocked", "done"];

export default function TaskDetailDrawer({ id }: { id: string }) {
  const { data: tasks = [] } = useTasks();
  const qc = useQueryClient();
  const task = tasks.find((t) => t.id === id);
  if (!task) return <div className="px-4 py-3 text-xs text-text-quiet">task not found</div>;

  const set = async (s: TaskStatus) => {
    await updateTaskStatus(task.id, s);
    qc.invalidateQueries({ queryKey: ["project_tasks"] });
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <div>
        <span
          className="font-data text-micro"
          style={{ letterSpacing: "0.13em", color: "var(--text-quiet)" }}
        >
          TASK · {task.project}
        </span>
        <h2 className="mt-1 text-base font-semibold text-text">{task.title}</h2>
      </div>
      {task.description ? (
        <p className="text-xs text-text-dim">{task.description}</p>
      ) : (
        <p className="text-xs text-text-quiet">No description.</p>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Field label="Kind" value={task.kind} />
        <Field label="Priority" value={String(task.priority)} />
        <Field label="Source" value={task.source} />
        <Field label="Owner" value={task.agent_owner ?? "Laura"} />
        <Field label="Created" value={relativeTime(task.created_at)} />
        <Field
          label="Completed"
          value={task.completed_at ? relativeTime(task.completed_at) : "—"}
        />
      </div>
      <div className="border-t border-border pt-3">
        <span
          className="font-data text-micro"
          style={{ letterSpacing: "0.13em", color: "var(--text-quiet)" }}
        >
          STATUS
        </span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => set(s)}
              className="font-data text-micro rounded-sm border px-2 py-1"
              style={{
                borderColor: s === task.status ? "var(--accent)" : "var(--border-strong)",
                backgroundColor: s === task.status ? "rgba(204,120,92,0.12)" : "transparent",
                color: s === task.status ? "var(--accent-strong)" : "var(--text-dim)",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded border border-border bg-surface px-2 py-1.5">
      <span
        className="font-data text-micro"
        style={{ letterSpacing: "0.1em", color: "var(--text-quiet)" }}
      >
        {label.toUpperCase()}
      </span>
      <span className="text-xs text-text">{value}</span>
    </div>
  );
}
